import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type ReactFlowInstance,
  MarkerType,
  ConnectionLineType,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";

import { useTheme } from "@/hooks/use-theme";
import { useWorkflowStore, type Trigger } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import {
  workflowToLayoutedElements,
  workflowToNodes,
  getLayoutedElements,
  type PlaygroundNode,
  type PlaygroundEdge,
} from "@/utils/playground";
import { logger } from "@/telemetry";

import { playgroundNodeTypes } from "./nodes";
import { playgroundEdgeTypes } from "./edges";
import PlaygroundDeleteConfirmation from "./PlaygroundDeleteConfirmation";

interface PlaygroundCanvasProps {
  onAutoLayout: React.MutableRefObject<(() => void) | null>;
}

const PlaygroundCanvas: React.FC<PlaygroundCanvasProps> = ({ onAutoLayout }) => {
  const { isDark } = useTheme();
  const { workflow, setWorkflow } = useWorkflowStore();
  const { setSelectedNode, setCanvasReady, nodeValidationErrors, nodeStatuses, workflowProgress } =
    usePlaygroundStore();
  const [nodes, setNodes, onNodesChange] = useNodesState<PlaygroundNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<PlaygroundEdge>([]);
  const reactFlowInstance = useRef<ReactFlowInstance<PlaygroundNode, PlaygroundEdge> | null>(null);

  // Deletion confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    nodeId: string;
    nodeType: string;
    connectionCount: number;
    nodesToDelete: PlaygroundNode[];
  } | null>(null);

  // Compute a structural fingerprint so we only re-layout when the graph
  // topology changes (nodes added/removed, connections changed), not on
  // data-only edits like updating a system message or temperature.
  const structuralFingerprint = useMemo(() => {
    if (!workflow) return null;

    const teamIds = Object.keys(workflow.teams || {});
    const teamMemberIds = new Set<string>();

    if (workflow.teams) {
      Object.values(workflow.teams).forEach((team) => {
        team.members.forEach((m) => {
          if (!workflow.teams?.[m]) teamMemberIds.add(m);
        });
      });
    }

    const agentIds = Object.keys(workflow.agents || {}).filter((id) => !teamMemberIds.has(id));

    const agentTools = Object.entries(workflow.agents || {})
      .filter(([id]) => !teamMemberIds.has(id))
      .map(
        ([id, agent]) => `${id}:${((agent.parameters?.tools as string[]) || []).sort().join(",")}`,
      )
      .sort();

    const agentTriggers = Object.entries(workflow.agents || {})
      .filter(([id]) => !teamMemberIds.has(id))
      .map(
        ([id, agent]) =>
          `${id}:${(agent.triggers || [])
            .map((t) => t.source)
            .sort()
            .join(",")}`,
      )
      .sort();

    const teamTriggers = Object.entries(workflow.teams || {})
      .map(
        ([id, team]) =>
          `${id}:${(team.triggers || [])
            .map((t) => t.source)
            .sort()
            .join(",")}`,
      )
      .sort();

    const teamMembers = Object.entries(workflow.teams || {})
      .map(([id, team]) => `${id}:${[...team.members].sort().join(",")}`)
      .sort();

    return JSON.stringify({
      agentIds: agentIds.sort(),
      teamIds: teamIds.sort(),
      agentTools,
      agentTriggers,
      teamTriggers,
      teamMembers,
    });
  }, [workflow]);

  // Load workflow into canvas — only re-layout when structure changes
  useEffect(() => {
    if (workflow) {
      try {
        const { nodes: layoutedNodes, edges: layoutedEdges } = workflowToLayoutedElements(
          workflow,
          "LR",
        );

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);

        // Fit view after layout
        setTimeout(() => {
          reactFlowInstance.current?.fitView({ padding: 0.15 });
        }, 100);
      } catch (error) {
        logger.error("Error loading workflow into canvas", error);
      }
    } else {
      setNodes([]);
      setEdges([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuralFingerprint, setNodes, setEdges]);

  // Sync node data (labels, parameters, etc.) when workflow changes without
  // structural changes — preserves existing positions.
  useEffect(() => {
    if (!workflow) return;

    const freshNodes = workflowToNodes(workflow);
    const freshDataMap = new Map(freshNodes.map((n) => [n.id, n.data]));

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const freshData = freshDataMap.get(node.id);

        if (!freshData) return node;
        if (JSON.stringify(node.data) === JSON.stringify({ ...node.data, ...freshData }))
          return node;

        return { ...node, data: { ...node.data, ...freshData } };
      }),
    );
  }, [workflow, setNodes]);

  // Inject per-node validation errors into node data
  useEffect(() => {
    if (Object.keys(nodeValidationErrors).length === 0) {
      // Clear any existing validation errors from nodes
      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          node.data.validationErrors && node.data.validationErrors.length > 0
            ? { ...node, data: { ...node.data, validationErrors: [] } }
            : node,
        ),
      );

      return;
    }

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const errors = nodeValidationErrors[node.id] || [];
        const current = node.data.validationErrors || [];

        // Only update if changed
        if (JSON.stringify(errors) === JSON.stringify(current)) return node;

        return { ...node, data: { ...node.data, validationErrors: errors } };
      }),
    );
  }, [nodeValidationErrors, setNodes]);

  // Inject execution status into node data
  useEffect(() => {
    if (Object.keys(nodeStatuses).length === 0) {
      // Clear statuses from nodes
      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          node.data.status
            ? { ...node, data: { ...node.data, status: undefined, isCurrentStep: false } }
            : node,
        ),
      );

      return;
    }

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const status = nodeStatuses[node.id];
        const isCurrentStep = workflowProgress.currentStep === node.id;

        if (node.data.status === status && node.data.isCurrentStep === isCurrentStep) return node;

        return { ...node, data: { ...node.data, status, isCurrentStep } };
      }),
    );
  }, [nodeStatuses, workflowProgress.currentStep, setNodes]);

  // Expose auto-layout function
  useEffect(() => {
    onAutoLayout.current = () => {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        nodes,
        edges,
        "LR",
      );

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);

      setTimeout(() => {
        reactFlowInstance.current?.fitView({ padding: 0.15 });
      }, 50);
    };
  }, [nodes, edges, setNodes, setEdges, onAutoLayout]);

  const onInit = useCallback(
    (instance: ReactFlowInstance<PlaygroundNode, PlaygroundEdge>) => {
      reactFlowInstance.current = instance;
      setCanvasReady(true);
    },
    [setCanvasReady],
  );

  // Handle node selection
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: PlaygroundNode) => {
      setSelectedNode(node.id);
    },
    [setSelectedNode],
  );

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      // Update workflow model
      if (params.target && params.source && workflow) {
        const targetNode = nodes.find((n) => n.id === params.target);
        const sourceNode = nodes.find((n) => n.id === params.source);

        if (!targetNode || !sourceNode) return;

        // Tool → Agent connection
        if (targetNode.type === "agent" && sourceNode.type === "tool") {
          const toolId = sourceNode.data.toolId;

          if (toolId) {
            const currentTools =
              (workflow.agents[targetNode.id]?.parameters?.tools as string[]) || [];

            if (!currentTools.includes(toolId)) {
              setWorkflow({
                ...workflow,
                agents: {
                  ...workflow.agents,
                  [targetNode.id]: {
                    ...workflow.agents[targetNode.id],
                    parameters: {
                      ...workflow.agents[targetNode.id].parameters,
                      tools: [...currentTools, toolId],
                    },
                  },
                },
              });
            }
          }

          // Add edge (tool edges use default type)
          setEdges((eds) =>
            addEdge(
              {
                ...params,
                type: "trigger",
                animated: true,
                style: { strokeDasharray: "3,3", opacity: 0.6 },
                markerEnd: { type: MarkerType.ArrowClosed },
                data: { edgeType: "tool" },
              },
              eds,
            ),
          );

          return;
        }

        // Agent/Input/Team → Agent/Team connection (trigger)
        if (
          (targetNode.type === "agent" || targetNode.type === "team") &&
          (sourceNode.type === "agent" || sourceNode.type === "input" || sourceNode.type === "team")
        ) {
          // Input → first agent always uses prompt→prompt; all others use prompt→data
          const defaultMapping =
            sourceNode.type === "input" ? { prompt: "prompt" } : { prompt: "data" };

          if (targetNode.type === "agent" && workflow.agents[targetNode.id]) {
            const currentTriggers = workflow.agents[targetNode.id].triggers || [];
            const exists = currentTriggers.some((t: Trigger) => t.source === sourceNode.id);

            if (!exists) {
              setWorkflow({
                ...workflow,
                agents: {
                  ...workflow.agents,
                  [targetNode.id]: {
                    ...workflow.agents[targetNode.id],
                    triggers: [
                      ...currentTriggers,
                      { source: sourceNode.id, mapping: defaultMapping },
                    ],
                  },
                },
              });
            }
          } else if (
            targetNode.type === "team" &&
            workflow.teams &&
            workflow.teams[targetNode.id]
          ) {
            const currentTriggers = workflow.teams[targetNode.id].triggers || [];
            const exists = currentTriggers.some((t: Trigger) => t.source === sourceNode.id);

            if (!exists) {
              setWorkflow({
                ...workflow,
                teams: {
                  ...workflow.teams,
                  [targetNode.id]: {
                    ...workflow.teams[targetNode.id],
                    triggers: [
                      ...currentTriggers,
                      { source: sourceNode.id, mapping: defaultMapping },
                    ],
                  },
                },
              });
            }
          }

          // Add trigger edge with mapping data
          setEdges((eds) =>
            addEdge(
              {
                ...params,
                type: "trigger",
                animated: true,
                markerEnd: { type: MarkerType.ArrowClosed },
                data: { mapping: defaultMapping, edgeType: "trigger" },
              },
              eds,
            ),
          );

          return;
        }

        // Fallback for other connections
        setEdges((eds) =>
          addEdge(
            {
              ...params,
              type: "trigger",
              animated: true,
              markerEnd: { type: MarkerType.ArrowClosed },
            },
            eds,
          ),
        );
      }
    },
    [setEdges, nodes, workflow, setWorkflow],
  );

  // Handle edge deletion
  const onEdgesDelete = useCallback(
    (edgesToDelete: PlaygroundEdge[]) => {
      if (!workflow) return;

      let updatedWorkflow = { ...workflow };

      edgesToDelete.forEach((edge) => {
        const targetNode = nodes.find((n) => n.id === edge.target);
        const sourceNode = nodes.find((n) => n.id === edge.source);

        if (!targetNode || !sourceNode) return;

        // Tool → Agent: remove tool
        if (targetNode.type === "agent" && sourceNode.type === "tool") {
          const toolId = sourceNode.data.toolId;

          if (toolId && updatedWorkflow.agents[targetNode.id]) {
            const currentTools =
              (updatedWorkflow.agents[targetNode.id].parameters?.tools as string[]) || [];

            updatedWorkflow = {
              ...updatedWorkflow,
              agents: {
                ...updatedWorkflow.agents,
                [targetNode.id]: {
                  ...updatedWorkflow.agents[targetNode.id],
                  parameters: {
                    ...updatedWorkflow.agents[targetNode.id].parameters,
                    tools: currentTools.filter((t) => t !== toolId),
                  },
                },
              },
            };
          }
        }

        // Agent/Input → Agent: remove trigger
        if (
          targetNode.type === "agent" &&
          (sourceNode.type === "agent" || sourceNode.type === "input")
        ) {
          if (updatedWorkflow.agents[targetNode.id]) {
            const currentTriggers = updatedWorkflow.agents[targetNode.id].triggers || [];

            updatedWorkflow = {
              ...updatedWorkflow,
              agents: {
                ...updatedWorkflow.agents,
                [targetNode.id]: {
                  ...updatedWorkflow.agents[targetNode.id],
                  triggers: currentTriggers.filter((t: Trigger) => t.source !== sourceNode.id),
                },
              },
            };
          }
        }

        // → Team: remove trigger
        if (targetNode.type === "team" && updatedWorkflow.teams?.[targetNode.id]) {
          const currentTriggers = updatedWorkflow.teams[targetNode.id].triggers || [];

          updatedWorkflow = {
            ...updatedWorkflow,
            teams: {
              ...updatedWorkflow.teams,
              [targetNode.id]: {
                ...updatedWorkflow.teams[targetNode.id],
                triggers: currentTriggers.filter((t: Trigger) => t.source !== sourceNode.id),
              },
            },
          };
        }
      });

      setWorkflow(updatedWorkflow);
    },
    [nodes, workflow, setWorkflow],
  );

  // Perform actual node deletion from workflow
  const performNodeDeletion = useCallback(
    (nodesToDelete: PlaygroundNode[]) => {
      if (!workflow) return;

      let updatedWorkflow = { ...workflow };

      nodesToDelete.forEach((node) => {
        if (node.type === "agent" && updatedWorkflow.agents[node.id]) {
          const { [node.id]: _, ...remainingAgents } = updatedWorkflow.agents;

          updatedWorkflow = { ...updatedWorkflow, agents: remainingAgents };

          // Remove from team members
          if (updatedWorkflow.teams) {
            const updatedTeams = { ...updatedWorkflow.teams };

            Object.entries(updatedTeams).forEach(([teamId, team]) => {
              if (team.members.includes(node.id)) {
                updatedTeams[teamId] = {
                  ...team,
                  members: team.members.filter((m) => m !== node.id),
                };
              }
            });
            updatedWorkflow = { ...updatedWorkflow, teams: updatedTeams };
          }

          // Remove triggers referencing this agent
          const updatedAgents = { ...updatedWorkflow.agents };

          Object.entries(updatedAgents).forEach(([agentId, agent]) => {
            if (agent.triggers?.some((t: Trigger) => t.source === node.id)) {
              updatedAgents[agentId] = {
                ...agent,
                triggers: agent.triggers.filter((t: Trigger) => t.source !== node.id),
              };
            }
          });
          updatedWorkflow = { ...updatedWorkflow, agents: updatedAgents };
        }

        if (node.type === "team" && updatedWorkflow.teams?.[node.id]) {
          const { [node.id]: _, ...remainingTeams } = updatedWorkflow.teams;

          updatedWorkflow = {
            ...updatedWorkflow,
            teams: Object.keys(remainingTeams).length > 0 ? remainingTeams : undefined,
          };
        }
      });

      setWorkflow(updatedWorkflow);
    },
    [workflow, setWorkflow],
  );

  // Handle node deletion — show confirmation for connected nodes
  const onNodesDelete = useCallback(
    (nodesToDelete: PlaygroundNode[]) => {
      if (!workflow) return;

      // Only confirm for agent/team nodes with connections
      const significantNodes = nodesToDelete.filter((n) => n.type === "agent" || n.type === "team");

      if (significantNodes.length > 0) {
        const node = significantNodes[0];
        const connectionCount = edges.filter(
          (e) => e.source === node.id || e.target === node.id,
        ).length;

        if (connectionCount > 0) {
          setDeleteConfirmation({
            nodeId: node.id,
            nodeType: node.type || "agent",
            connectionCount,
            nodesToDelete,
          });

          return;
        }
      }

      // No confirmation needed — delete directly
      performNodeDeletion(nodesToDelete);
    },
    [workflow, edges, performNodeDeletion],
  );

  // Handle delete confirmation
  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmation) {
      performNodeDeletion(deleteConfirmation.nodesToDelete);
      setDeleteConfirmation(null);
    }
  }, [deleteConfirmation, performNodeDeletion]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  return (
    <div className="h-full w-full">
      <ReactFlow
        fitView
        className={isDark ? "dark" : ""}
        colorMode={isDark ? "dark" : "light"}
        connectionLineType={ConnectionLineType.SmoothStep}
        defaultEdgeOptions={{
          animated: true,
          type: "trigger",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        edgeTypes={playgroundEdgeTypes}
        edges={edges}
        maxZoom={2}
        minZoom={0.2}
        nodeTypes={playgroundNodeTypes}
        nodes={nodes}
        onConnect={onConnect}
        onEdgesChange={onEdgesChange}
        onEdgesDelete={onEdgesDelete}
        onInit={onInit}
        onNodeClick={onNodeClick}
        onNodesChange={onNodesChange}
        onNodesDelete={onNodesDelete}
        onPaneClick={onPaneClick}
      >
        <Background gap={20} />
        <Controls className="!shadow-lg !rounded-lg" />
        <MiniMap
          className="!shadow-lg !rounded-lg"
          maskColor={isDark ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.1)"}
          nodeColor={(node) => {
            switch (node.type) {
              case "agent":
                return "#3b82f6";
              case "team":
                return "#a855f7";
              case "tool":
                return "#6b7280";
              case "input":
                return "#78716c";
              case "output":
                return "#22c55e";
              default:
                return "#6b7280";
            }
          }}
        />
      </ReactFlow>

      {/* Delete confirmation dialog */}
      {deleteConfirmation && (
        <PlaygroundDeleteConfirmation
          connectionCount={deleteConfirmation.connectionCount}
          isOpen={!!deleteConfirmation}
          nodeId={deleteConfirmation.nodeId}
          nodeType={deleteConfirmation.nodeType}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default PlaygroundCanvas;
